// Shared auto-dismiss for the Mirror's post-action confirmation surfaces.
//
// Three surfaces tell the user "the Mirror did the thing", then must get out of
// the way so the voice loop isn't trapped (the bug: cards only cleared on a
// screen switch). They now share ONE source of truth for "how long" and "how to
// arm the timer":
//
//   1. deterministic-nav toast (#253)  -> NAV_DISMISS_MS  (sonner `duration`)
//   2. capture-recap bubbles (#256)    -> CARD_DISMISS_MS (scheduleAutoDismiss)
//   3. agenda "evento criado" card     -> CARD_DISMISS_MS (useAutoDismiss)
//
// The nav toast is a sonner toast (app-level, survives navigation), so it CANNOT
// be driven by an in-tree React hook — it consumes the shared CONSTANT via
// sonner's own `duration`. The recap is a multi-item list where each bubble
// schedules its own removal, so it uses the imperative `scheduleAutoDismiss`
// primitive. The agenda card is a single "active" value, the natural fit for the
// `useAutoDismiss` hook. All three pull from this module — same numbers, same
// timer semantics, no per-surface drift.
//
// Invariant the bug demanded: dismissal depends ONLY on the surface's own timer,
// never on a route change or session reload.

import { useEffect, useRef } from "react";

/** Deterministic-nav toast ("Te levando pro {módulo}…"). Short — it only
 *  confirms a navigation that already happened. */
export const NAV_DISMISS_MS = 2000;

/** In-tree confirmation cards (capture recap, agenda "evento criado"). A touch
 *  longer so the user can read the recap before it clears. */
export const CARD_DISMISS_MS = 3000;

/**
 * Auto-dismiss a single active confirmation surface after `ms`.
 *
 * `activeKey` is the surface's identity: any truthy value (a non-empty string,
 * an id, an object) means "shown" and arms a one-shot timer; a falsy/empty value
 * (`""`, `null`, `undefined`, `0`, `false`) means "nothing shown" and arms
 * nothing. When `activeKey` changes to a NEW truthy value the previous timer is
 * cleared and a fresh one starts, so a new confirmation always gets the full
 * window (no half-spent timer from the prior one).
 *
 * `onClear` is read through a ref, so callers may pass a fresh inline closure on
 * every render without restarting the timer — only `activeKey` / `ms` restart
 * it. The effect's cleanup clears ONLY this timer: dismissal never depends on a
 * route change or session reload.
 */
export function useAutoDismiss(
  activeKey: unknown,
  ms: number,
  onClear: () => void,
): void {
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useEffect(() => {
    if (!activeKey) return;
    const id = window.setTimeout(() => onClearRef.current(), ms);
    return () => window.clearTimeout(id);
  }, [activeKey, ms]);
}

/**
 * Imperative one-shot variant for surfaces that aren't a single React "active"
 * value — e.g. the multi-item capture-recap list, where each appended bubble
 * schedules its OWN removal independently. Returns a canceller; call it on
 * unmount (or to abort early). Same timer semantics as the hook, no React
 * lifecycle assumed.
 */
export function scheduleAutoDismiss(
  ms: number,
  onClear: () => void,
): () => void {
  const id = window.setTimeout(onClear, ms);
  return () => window.clearTimeout(id);
}
